import { useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Kanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getOriginChannels } from "@/lib/originChannels";
import {
  duplicateCompanyMessage,
  findExistingCompany,
} from "@/lib/duplicateIdentity";
import { isNaiuTenant } from "@/lib/tenant";
import { applyDealStageEffects } from "@/lib/applyDealStageEffects";
import type { Company, Contact, Deal } from "../types";
import { getContactDisplayName } from "../contacts/contactDisplay";

interface StartDealButtonProps {
  company?: Company;
  contact?: Contact;
}

export function StartDealButton({ company, contact }: StartDealButtonProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const [isOpen, setIsOpen] = useState(false);
  const [origin, setOrigin] = useState(company?.origin || "call");
  const [isPending, setIsPending] = useState(false);
  const companyId = company?.id ?? contact?.company_id;
  const { data: openDeals = [] } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 1 },
      filter: {
        company_id: companyId,
        "archived_at@is": null,
        "stage@neq": "lost",
      },
    },
    { enabled: isOpen && companyId != null },
  );

  const handleStart = async (): Promise<void> => {
    const salesId = identity?.id;
    if (salesId == null) {
      notify("crm.deals.start_error", {
        type: "error",
        messageArgs: { _: "No hay un vendedor en sesión." },
      });
      return;
    }

    setIsPending(true);
    const closingDate = new Date();
    closingDate.setDate(closingDate.getDate() + 14);
    const expectedClosingDate = closingDate.toISOString().split("T")[0];

    try {
      let resolvedCompanyId = company?.id ?? contact?.company_id;
      let resolvedName =
        company?.name ??
        contact?.company_name ??
        (contact ? getContactDisplayName(contact) : "");

      if (resolvedCompanyId == null && contact && !isNaiuTenant()) {
        const existingCompany = await findExistingCompany(dataProvider, {
          name: resolvedName,
        });
        if (existingCompany?.id != null) {
          resolvedCompanyId = existingCompany.id;
          notify(
            duplicateCompanyMessage(existingCompany.name ?? resolvedName),
            {
              type: "warning",
            },
          );
        } else {
          const created = await dataProvider.create<Company>("companies", {
            data: {
              name: resolvedName,
              origin,
              sales_id: salesId,
              description: "",
            },
          });
          resolvedCompanyId = created.data.id;
          resolvedName = created.data.name;
        }
      }

      if (!resolvedName || (resolvedCompanyId == null && !isNaiuTenant())) {
        notify("crm.deals.start_error", {
          type: "error",
          messageArgs: { _: "Falta la empresa o el contacto." },
        });
        return;
      }

      const created = await dataProvider.create<Deal>("deals", {
        data: {
          name: resolvedName,
          company_id: resolvedCompanyId ?? null,
          contact_ids: contact?.id != null ? [contact.id] : [],
          category: isNaiuTenant() ? "detal" : "other",
          product: "other_custom",
          stage: "captured",
          description: "",
          amount: 0,
          currency: "COP",
          billing_type: "one_time",
          expected_closing_date: expectedClosingDate,
          sales_id: salesId,
          index: 0,
          origin,
        },
      });
      await applyDealStageEffects(dataProvider, created.data, "captured");

      notify("crm.deals.started", {
        type: "info",
        messageArgs: { _: "Negocio creado en Captado." },
      });
      setIsOpen(false);
      redirect("/deals");
    } catch {
      notify("crm.deals.start_error", {
        type: "error",
        messageArgs: { _: "No se pudo crear el negocio." },
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        className="h-9"
        onClick={() => setIsOpen(true)}
      >
        <Kanban className="h-4 w-4" aria-hidden="true" />
        {translate("crm.deals.start", { _: "Iniciar negocio" })}
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate("crm.deals.start_title", { _: "Pasar a Captado" })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {translate("crm.deals.start_help", {
              _: "Elige cómo contactaste a este cliente. El negocio entra al pipeline en Captado, sin precio todavía.",
            })}
          </p>
          {openDeals.length > 0 ? (
            <p className="text-sm text-[#7EC8E3]">
              {translate("crm.deals.already_open", {
                _: "Ya hay un negocio abierto. Puedes crear otro si es una venta distinta.",
              })}
            </p>
          ) : null}
          <label className="flex flex-col gap-2 text-sm" htmlFor="deal-origin">
            {translate("resources.deals.fields.origin", {
              _: "Canal de origen",
            })}
            <select
              id="deal-origin"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={origin}
              onChange={(event) => setOrigin(event.target.value)}
            >
              {getOriginChannels().map((channel) => (
                <option key={channel.value} value={channel.value}>
                  {channel.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => {
              void handleStart();
            }}
          >
            {translate("crm.deals.start_confirm", { _: "Crear en Captado" })}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
